import { FilterPanelBaseFilter } from "app/pages/sales-intelligence/modules/advanced-search-filtering/models/BaseFilter";
import type { IFilterInstance } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { SdkUpgradeView } from "app/pages/sales-intelligence/modules/apps-search-filters/components/upgrade-views/SdkUpgradeView";
import { appOperatingSystemMap } from "app/pages/sales-intelligence/modules/apps-search-filters/types/commonFiltersDto";
import createAdsSellersFilter from "app/pages/sales-intelligence/modules/find-apps-filters/models/AdsSellersFilter/factory";
import createIabCategoriesFilter from "app/pages/sales-intelligence/modules/find-apps-filters/models/IabCategoriesFilter/factory";
import createListedAddressFilter from "app/pages/sales-intelligence/modules/find-apps-filters/models/ListedAddressFilter/factory";
import createPermissionsFilter from "app/pages/sales-intelligence/modules/find-apps-filters/models/PermissionsFilter/factory";
import createPriceFilter from "app/pages/sales-intelligence/modules/find-apps-filters/models/PriceFilter/factory";
import FindAppsRatingFilter from "app/pages/sales-intelligence/modules/find-apps-filters/models/RatingFilter";
import createUpdateDateFilter from "app/pages/sales-intelligence/modules/find-apps-filters/models/UpdateDateFilter/factory";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import type {
    IAppsAndroidCategoryFilter,
    IAppsAndroidFiltersDto,
    IAppsAndroidIdentifierFilter,
} from "app/pages/sales-intelligence/store/find-apps/types/androidFilters.dto";
import type { IAppsSDKsFilterItem } from "app/pages/sales-intelligence/store/find-apps/types/commonFilters.dto";

import AppAndroidCategoryFilter from "../components/filters/AppAndroidCategoryFilter/AppAndroidCategoryFilter";
import AppAndroidCategoryFilterResult from "../components/filters/AppAndroidCategoryFilter/AppAndroidCategoryFilterResult";
import AppSdkFilter from "../components/filters/AppSdkFilter/AppSdkFilter";
import AppSdkFilterResult from "../components/filters/AppSdkFilter/AppSdkFilterResult";
import { AppsAndroidIdentifiersFilter } from "../components/filters/AppsIdentifiersFilter/android/AppsAndroidIdentifiersFilter";
import { AppsAndroidIdentifiersFilterResult } from "../components/filters/AppsIdentifiersFilter/android/AppsAndroidIdentifiersFilterResult";
import { AppsRatingFilter } from "../components/filters/AppsRatingFilter/AppsRatingFilter";
import { AppsRatingFilterResult } from "../components/filters/AppsRatingFilter/AppsRatingFilterResult";
import createAndroidDownloadAndEngagementFilter from "../models/AndroidDownloadAndEngagementFilter/factory";
import createBusinessModelFilter from "../models/BusinessModelFilter/factory";
import type {
    AppsAndroidCategoryDescriptionDto,
    AppsSDKsFilterDescriptionDto,
    FindAppsDescriptionDto,
} from "../types/description.dto";
import { getSdkFilterByPlatform } from "../utils";

export const createAndroidFilterConfig = (
    description?: FindAppsDescriptionDto,
): IFilterInstance<IAppsAndroidFiltersDto>[] => {
    if (!description) {
        return [];
    }

    return [
        {
            key: "category",
            trackingName: "App Category",
            title: { key: "si.find_apps.filters.android-category.title" },
            tooltipText: { key: "si.find_apps.filters.android-category.tooltip" },
            filter: new FilterPanelBaseFilter<IAppsAndroidCategoryFilter, AppsAndroidCategoryDescriptionDto>({
                options: description?.androidCategory || {
                    categories: [],
                },
            }),
            filterView: AppAndroidCategoryFilter,
            filterResultView: AppAndroidCategoryFilterResult,
        },
        createIabCategoriesFilter(description),
        createUpdateDateFilter(description),
        {
            key: "sdks",
            trackingName: "App SDKs",
            title: { key: "si.find_apps.filters.sdks.title" },
            tooltipText: { key: "si.find_apps.filters.sdks.tooltip" },
            gateStatus: !salesSettingsHelper.isAppsSdksAvailable
                ? { type: "upgrade", view: SdkUpgradeView }
                : undefined,
            filter: new FilterPanelBaseFilter<IAppsSDKsFilterItem[], AppsSDKsFilterDescriptionDto>({
                options: getSdkFilterByPlatform(description?.sdksFilter, appOperatingSystemMap.android) || {
                    sdksDescriptions: [],
                    inclusionOptions: [],
                    operatorOptions: [],
                },
            }),
            filterView: AppSdkFilter,
            filterResultView: AppSdkFilterResult,
        },
        createAdsSellersFilter(description.androidAdsSellers),
        createPermissionsFilter(description.androidPermission),
        createListedAddressFilter(),
        createAndroidDownloadAndEngagementFilter(description),
        {
            key: "rating",
            trackingName: "App Rating",
            title: { key: "si.find_apps.filters.rating.title" },
            tooltipText: { key: "si.find_apps.filters.rating.tooltip" },
            filter: new FindAppsRatingFilter({
                options: description?.rating || {
                    numberOfRatingsBuckets: [],
                },
            }),
            filterView: AppsRatingFilter,
            filterResultView: AppsRatingFilterResult,
        },
        // TODO remove when BE fix is released
        // @ts-expect-error incorrect BE model
        createBusinessModelFilter(description.appBusinessModels ?? description.businessModels),
        createPriceFilter(description),
        {
            key: "identifier",
            trackingName: "App Identifier",
            title: { key: "si.find_apps.filters.identifier.title" },
            tooltipText: { key: "si.find_apps.filters.identifier.android.tooltip" },
            filter: new FilterPanelBaseFilter<IAppsAndroidIdentifierFilter>({
                options: [],
            }),
            filterView: AppsAndroidIdentifiersFilter,
            filterResultView: AppsAndroidIdentifiersFilterResult,
        },
    ];
};
